import { ChannelIcon } from '@/shared/components'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import BlueCheckmarkIcon from '@/shared/assets/icons/checkmarkBlue.svg'
import './selectDropdown.scss'
import { PlusIcon } from 'lucide-react'
import { ChevronDownSVGIcon } from '@/shared/assets'

export type SelectDropdownOptionType<V, L, Extra = object> = {
    userId?: number
    channelId?: number
    value: V
    label: L
    username?: string
    photoUrl?: string | null
} & Extra

interface SelectDropdownProps<V, L, Extra = object> {
    options: ReadonlyArray<SelectDropdownOptionType<V, L, Extra>>
    onSelect: (option: SelectDropdownOptionType<V, L, Extra>) => void
    value: V | V[]
    placeholder?: string
    className?: string
    disabled?: boolean
    noArrow?: boolean
    disableOpacity?: boolean
    headerClassName?: string
    listClassName?: string
    labelClassName?: string
    onPlusButtonClick?: () => void
    isMulti?: boolean
    error?: boolean
    renderOption?: (
        option: SelectDropdownOptionType<V, L, Extra>,
        selected: boolean
    ) => React.ReactNode
}

export const SelectDropdown = <
    V extends string,
    L extends string,
    Extra = object,
>({
    options,
    onSelect,
    value,
    placeholder = 'Select an option',
    className = '',
    disabled = false,
    noArrow = false,
    disableOpacity = false,
    headerClassName = '',
    listClassName = '',
    labelClassName = '',
    onPlusButtonClick,
    isMulti = false,
    error,
    renderOption,
}: SelectDropdownProps<V, L, Extra>) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const values = Array.isArray(value) ? value : [value]

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleToggle = () => {
        if (!disabled) setIsOpen(!isOpen)
    }

    const handleSelect = (option: SelectDropdownOptionType<V, L, Extra>) => {
        if (!disabled) {
            if (isMulti) {
                const newValue = values.includes(option.value)
                    ? values.filter((v) => v !== option.value)
                    : [...values, option.value]
                onSelect({ ...option, value: newValue as any })
            } else {
                onSelect(option)
                setIsOpen(false)
            }
        }
    }

    const selectedOptions = isMulti
        ? options.filter((option) => values.includes(option.value))
        : [options.find((option) => option.value === value)].filter(Boolean)

    return (
        <div
            className={clsx('select-dropdown', className, {
                disabled,
                disableOpacity,
            })}
            ref={dropdownRef}
        >
            <div
                className={clsx('select-dropdown__header', headerClassName, {
                    active: isOpen,
                    'select-dropdown__header--error': !!error,
                })}
                data-value={selectedOptions[0]?.value}
                onClick={handleToggle}
            >
                {!isMulti &&
                    selectedOptions[0]?.username &&
                    selectedOptions[0]?.channelId && (
                        <ChannelIcon
                            photoUrl={selectedOptions[0].photoUrl}
                            username={selectedOptions[0].username}
                            className="select-dropdown__header__icon"
                        />
                    )}
                <span
                    className={clsx(
                        'select-dropdown__header__label',
                        labelClassName
                    )}
                >
                    {selectedOptions.length > 0
                        ? isMulti
                            ? selectedOptions
                                  .map((opt) => opt?.label || '')
                                  .join(', ')
                            : selectedOptions[0]?.label || ''
                        : placeholder}
                </span>
                {onPlusButtonClick && noArrow && (
                    <button
                        className="select-dropdown__header__plus-button"
                        onClick={(e) => {
                            e.stopPropagation()
                            onPlusButtonClick()
                        }}
                    >
                        <PlusIcon className="select-dropdown__header__plus-button__icon" />
                    </button>
                )}
                {noArrow ? null : (
                    <ChevronDownSVGIcon
                        className={clsx('select-dropdown__header__arrow', {
                            open: isOpen,
                        })}
                    />
                )}
            </div>
            <ul
                className={clsx('select-dropdown__list', listClassName, {
                    show: isOpen,
                })}
            >
                {options.map((option) => {
                    const isSelected = isMulti
                        ? values.includes(option.value)
                        : option.value === value
                    return (
                        <li
                            key={option.value}
                            className={
                                renderOption
                                    ? undefined
                                    : clsx('select-dropdown__item', {
                                          selected: isSelected,
                                      })
                            }
                            data-value={option.value}
                            onClick={() => handleSelect(option)}
                        >
                            {renderOption ? (
                                renderOption(option, isSelected)
                            ) : (
                                <>
                                    {option?.username &&
                                        option?.userId &&
                                        option?.channelId && (
                                            <ChannelIcon
                                                photoUrl={option.photoUrl}
                                                username={option.username}
                                                className="select-dropdown__item__icon"
                                            />
                                        )}
                                    <span className="select-dropdown__item__label">
                                        {option.label}
                                    </span>
                                    {isSelected && (
                                        <img
                                            src={BlueCheckmarkIcon}
                                            className="select-dropdown__item__checkmark"
                                            alt="Selected"
                                        />
                                    )}
                                </>
                            )}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
